'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  q: string;
  a: React.ReactNode;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const FAQ_SECTIONS: FAQSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        q: 'What is StocksIQPro?',
        a: 'StocksIQPro is a free, independent financial research platform offering real-time stock and ETF quotes, a market heatmap, screener, watchlist, earnings and IPO calendars, market movers, company discussions, news feed, and compare tools — all in one place.',
      },
      {
        q: 'Is StocksIQPro free?',
        a: 'Yes. Core features are completely free for all visitors. A Pro subscription unlocks additional capabilities such as advanced screener filters, expanded API access, and priority data refresh rates.',
      },
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" in the top navigation bar. Enter your email address and a password, verify your email, and you\'re all set. Account creation is instant and free.',
      },
    ],
  },
  {
    title: 'Features',
    items: [
      {
        q: 'Where can I look up stock or ETF data?',
        a: 'Use the search bar at the top of any page to search by ticker or company name. You can also browse the Stocks and ETFs overview pages for a paginated list with sort and filter options.',
      },
      {
        q: 'What is the Market Heatmap?',
        a: 'The Market Heatmap is a visual overview of the entire stock market, organized by sector and colored by daily performance. Green tiles indicate gains, red tiles indicate losses, and size represents market capitalization.',
      },
      {
        q: 'What are Market Movers?',
        a: 'Market Movers shows the top performing and declining stocks each day: Gainers, Losers, Most Active, Pre-Market, and After-Hours movers. Find them under the Markets menu.',
      },
      {
        q: 'How does the Watchlist work?',
        a: 'After creating a free account, you can add any stock or ETF to your watchlist by clicking the star icon on a detail page. Your watchlist is available from your dashboard for quick monitoring.',
      },
      {
        q: 'What filters does the Screener support?',
        a: 'The stock screener lets you filter by sector, exchange, market cap range, P/E ratio, volume, price, and more. Pro subscribers get access to additional advanced filters.',
      },
      {
        q: 'What does the Compare tool do?',
        a: 'The Compare tool lets you select two or more stocks or ETFs and see them side-by-side across key metrics: price, market cap, P/E ratio, 52-week range, dividend yield, analyst rating, and more.',
      },
      {
        q: 'What is the Earnings Calendar?',
        a: 'The Earnings Calendar shows upcoming earnings report dates for publicly traded companies, including estimated EPS and revenue consensus. You can browse by date range to plan ahead.',
      },
      {
        q: 'What is the IPO Calendar?',
        a: 'The IPO Calendar lists upcoming and recent initial public offerings with pricing ranges, expected exchange listing, sector, and post-IPO performance data where available.',
      },
      {
        q: 'How do Discussions work?',
        a: 'Each stock and ETF has a community discussion thread. Registered users can post comments, reply to threads, and engage with other investors. Content is moderated to keep discussions constructive.',
      },
      {
        q: 'Where does the News Feed come from?',
        a: 'The news feed aggregates financial news headlines from multiple sources. You can view market-wide news or filter by a specific ticker on any stock detail page.',
      },
    ],
  },
  {
    title: 'Pro Subscription',
    items: [
      {
        q: 'What is included in the Pro subscription?',
        a: 'Pro includes advanced screener filters, higher API rate limits for programmatic access, faster data refresh rates, and priority support. Additional Pro features are added regularly.',
      },
      {
        q: 'How do I upgrade to Pro?',
        a: 'Log in to your account, go to your profile settings, and click "Upgrade to Pro." You\'ll be directed to a secure payment flow powered by Stripe.',
      },
      {
        q: 'What does Pro cost?',
        a: 'Pricing details are displayed on the upgrade page. We aim to keep the cost affordable relative to comparable institutional tools.',
      },
    ],
  },
  {
    title: 'Data & Accuracy',
    items: [
      {
        q: 'Where does the market data come from?',
        a: 'StocksIQPro aggregates data from multiple financial data providers. We work to ensure accuracy, but data is provided for informational purposes only and should not be taken as financial advice.',
      },
      {
        q: 'Is the data real-time or delayed?',
        a: 'Quote data may be real-time or delayed by up to 15 minutes depending on the data source and exchange. Data refresh intervals are displayed where applicable.',
      },
      {
        q: 'Is this financial advice?',
        a: 'No. StocksIQPro provides data and research tools for informational purposes only. Nothing on the platform constitutes financial, investment, or trading advice. Always do your own research and consult a qualified financial advisor.',
      },
    ],
  },
  {
    title: 'Support & Donations',
    items: [
      {
        q: 'How can I support StocksIQPro?',
        a: 'The best ways to support us are: (1) upgrading to a Pro subscription, (2) making a voluntary donation via PayPal, Venmo, or Zelle — details are on the About page, and (3) spreading the word to other investors.',
      },
      {
        q: 'How do I contact support?',
        a: 'Use the contact form on our site or reach out via the discussion boards. We\'re a small team and aim to respond within 1–2 business days.',
      },
    ],
  },
];

function AccordionItem({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-default last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left text-white hover:text-sky-400 transition-colors"
      >
        <span className="font-medium text-sm leading-snug">{item.q}</span>
        {open ? (
          <ChevronUp size={16} className="flex-shrink-0 text-sky-400" />
        ) : (
          <ChevronDown size={16} className="flex-shrink-0 text-fg-subtle" />
        )}
      </button>
      {open && (
        <div className="pb-4 text-fg-muted text-sm leading-relaxed pr-6">
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-surface text-white">
      {/* Header */}
      <div className="border-b border-card-bg">
        <div className="max-w-3xl mx-auto px-3 py-12 text-center">
          <h1 className="text-3xl font-bold text-white mb-3">Frequently Asked Questions</h1>
          <p className="text-fg-muted text-base">
            Everything you need to know about StocksIQPro.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 py-12 space-y-10">
        {FAQ_SECTIONS.map((section) => (
          <section key={section.title}>
            <h2 className="text-xs font-bold uppercase tracking-widest text-sky-400 mb-4">
              {section.title}
            </h2>
            <div className="bg-card-bg border border-default rounded-xl px-6">
              {section.items.map((item) => (
                <AccordionItem key={item.q} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
